<script setup>
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'MasterData',
  },
})

const ability = useAbility()

const items = ref([])
const loading = ref(false)
const totalItems = ref(0)
const page = ref(1)
const perPage = ref(15)
const search = ref('')

const dialog = ref(false)
const dialogTitle = ref('')
const editingId = ref(null)
const saving = ref(false)
const errors = ref({})

const formDefault = () => ({
  employee_number: '', name: '', position_id: null, unit_id: null,
  email: '', phone: '', join_date: '', employment_status: 'active', is_active: true,
  create_login_account: false, roles: [],
})

const form = ref(formDefault())
const editingHasLogin = ref(false)

const statusOptions = [
  { title: 'Aktif', value: 'active' },
  { title: 'Nonaktif', value: 'inactive' },
  { title: 'Resign', value: 'resigned' },
]

const roles = ref([])
const roleItems = computed(() => roles.value.map(r => ({ title: r.name, value: r.name })))

const fetchRoles = async () => {
  try {
    const response = await useApi('/api/v1/roles').get()
    const data = response?.data ?? response

    roles.value = Array.isArray(data) ? data : (data?.data ?? [])
  } catch (e) {
    console.error('Fetch roles error:', e)
  }
}

const deleteDialog = ref(false)
const deletingItem = ref(null)
const deleting = ref(false)

// Password yang baru dibuat ditampilkan SEKALI (tidak pernah disimpan
// mentah di frontend, tidak bisa diambil ulang setelah dialog ini ditutup).
const generatedPasswordDialog = ref(false)
const generatedPasswordInfo = ref({ email: '', password: '' })

const fetchItems = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (search.value) query.search = search.value

    const response = await useApi('/api/v1/master-data/employees', { query }).get()
    const data = response?.data ?? response

    items.value = Array.isArray(data) ? data : (data?.data ?? [])
    totalItems.value = data?.meta?.total ?? data?.total ?? 0
  } catch (e) {
    console.error('Fetch employees error:', e)
  } finally {
    loading.value = false
  }
}

onMounted(() => { fetchItems(); fetchRoles() })
watch(search, () => { page.value = 1; fetchItems() })
watch(page, fetchItems)

const openCreate = () => {
  editingId.value = null
  form.value = formDefault()
  editingHasLogin.value = false
  errors.value = {}
  dialogTitle.value = 'Tambah Pegawai'
  dialog.value = true
}

const openEdit = item => {
  editingId.value = item.id
  form.value = {
    employee_number: item.employee_number,
    name: item.name,
    position_id: item.position_id,
    unit_id: item.unit_id,
    email: item.email ?? '',
    phone: item.phone ?? '',
    join_date: item.join_date ?? '',
    employment_status: item.employment_status,
    is_active: item.is_active,
    create_login_account: false,
    roles: [],
  }
  editingHasLogin.value = !!item.user_id
  errors.value = {}
  dialogTitle.value = 'Edit Pegawai'
  dialog.value = true
}

const save = async () => {
  saving.value = true
  errors.value = {}
  try {
    const api = useApi(editingId.value ? `/api/v1/master-data/employees/${editingId.value}` : '/api/v1/master-data/employees')
    const response = editingId.value ? await api.put({ body: form.value }) : await api.post({ body: form.value })
    const saved = response?.data ?? response
    const generatedPassword = response?.generated_password

    if (editingId.value) {
      const idx = items.value.findIndex(i => i.id === editingId.value)
      if (idx !== -1) items.value[idx] = saved
    } else {
      fetchItems()
    }
    dialog.value = false

    if (generatedPassword) {
      generatedPasswordInfo.value = { email: saved?.email ?? saved?.user?.email ?? '', password: generatedPassword }
      generatedPasswordDialog.value = true
    }
  } catch (e) {
    if (e?.status === 422) errors.value = e?.data?.errors ?? {}
    else console.error('Save employee error:', e)
  } finally {
    saving.value = false
  }
}

const confirmDelete = item => { deletingItem.value = item; deleteDialog.value = true }

const doDelete = async () => {
  deleting.value = true
  try {
    await useApi(`/api/v1/master-data/employees/${deletingItem.value.id}`).delete()
    deleteDialog.value = false
    fetchItems()
  } catch (e) {
    console.error('Delete employee error:', e)
  } finally {
    deleting.value = false
  }
}

const statusColor = value => ({ active: 'success', inactive: 'secondary', resigned: 'error' }[value] ?? 'secondary')
const statusLabel = value => statusOptions.find(s => s.value === value)?.title ?? value

const headers = [
  { title: 'No. Pegawai', key: 'employee_number', width: 130 },
  { title: 'Nama', key: 'name' },
  { title: 'Jabatan', key: 'position' },
  { title: 'Unit', key: 'unit' },
  { title: 'Status', key: 'employment_status', align: 'center', width: 120 },
  { title: 'Aksi', key: 'actions', align: 'center', sortable: false, width: 120 },
]
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">
              Pegawai
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Master data pegawai
            </p>
          </div>
          <VBtn
            v-if="ability.can('manage', 'MasterData')"
            prepend-icon="ri-add-line"
            @click="openCreate"
          >
            Tambah Pegawai
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <VCard class="mb-4">
      <VCardText>
        <VTextField
          v-model="search"
          placeholder="Cari nama atau nomor pegawai..."
          prepend-inner-icon="ri-search-line"
          clearable
          hide-details
          density="compact"
          style="max-width: 320px;"
        />
      </VCardText>
    </VCard>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="perPage"
        hide-default-footer
      >
        <template #item.position="{ item }">
          {{ item.position?.name ?? '-' }}
        </template>
        <template #item.unit="{ item }">
          {{ item.unit?.name ?? '-' }}
        </template>
        <template #item.employment_status="{ item }">
          <VChip
            size="small"
            :color="statusColor(item.employment_status)"
          >
            {{ statusLabel(item.employment_status) }}
          </VChip>
        </template>
        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('manage', 'MasterData')"
            icon="ri-pencil-line"
            variant="text"
            size="small"
            color="primary"
            @click="openEdit(item)"
          />
          <VBtn
            v-if="ability.can('manage', 'MasterData')"
            icon="ri-delete-bin-line"
            variant="text"
            size="small"
            color="error"
            @click="confirmDelete(item)"
          />
        </template>
        <template #no-data>
          <div class="text-center py-10">
            <p class="text-medium-emphasis">
              Belum ada data
            </p>
          </div>
        </template>
      </VDataTable>
      <VDivider />
      <VCardText class="d-flex align-center justify-space-between flex-wrap gap-3 pa-4">
        <span class="text-body-2 text-medium-emphasis">Total: {{ totalItems }}</span>
        <VPagination
          v-if="totalItems > perPage"
          v-model="page"
          :length="Math.ceil(totalItems / perPage)"
          :total-visible="5"
          density="compact"
        />
      </VCardText>
    </VCard>

    <VDialog
      v-model="dialog"
      max-width="640"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          {{ dialogTitle }}
        </VCardTitle>
        <VCardText class="pa-6">
          <VRow>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.employee_number"
                label="Nomor Pegawai *"
                :error-messages="errors.employee_number"
                autofocus
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.name"
                label="Nama *"
                :error-messages="errors.name"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <MasterDataPicker
                v-model="form.position_id"
                entity="positions"
                label="Jabatan"
                :error-messages="errors.position_id"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <MasterDataPicker
                v-model="form.unit_id"
                entity="units"
                label="Unit"
                :error-messages="errors.unit_id"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.email"
                label="Email"
                type="email"
                :error-messages="errors.email"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.phone"
                label="Telepon"
                :error-messages="errors.phone"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.join_date"
                label="Tanggal Bergabung"
                type="date"
                :error-messages="errors.join_date"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VSelect
                v-model="form.employment_status"
                :items="statusOptions"
                label="Status Kepegawaian *"
                :error-messages="errors.employment_status"
              />
            </VCol>
            <VCol cols="12">
              <VSwitch
                v-model="form.is_active"
                label="Aktif"
                color="success"
                hide-details
              />
            </VCol>

            <template v-if="!editingHasLogin">
              <VCol cols="12">
                <VDivider class="mb-2" />
                <VSwitch
                  v-model="form.create_login_account"
                  label="Buatkan akun login untuk pegawai ini"
                  color="primary"
                  hide-details
                />
                <p class="text-caption text-medium-emphasis mt-1 mb-0">
                  Nama & email akun login otomatis memakai data pegawai di atas — tidak perlu diketik ulang. Password akan dibuatkan otomatis dan ditampilkan sekali setelah disimpan.
                </p>
              </VCol>
              <VCol
                v-if="form.create_login_account"
                cols="12"
              >
                <VSelect
                  v-model="form.roles"
                  :items="roleItems"
                  label="Role *"
                  multiple
                  chips
                  closable-chips
                  :error-messages="errors.roles"
                />
              </VCol>
            </template>
          </VRow>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="dialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="save"
          >
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="deleteDialog"
      max-width="420"
    >
      <VCard>
        <VCardTitle class="pa-6 pb-2">
          Hapus Pegawai?
        </VCardTitle>
        <VCardText class="pa-6 pt-0">
          <strong>{{ deletingItem?.name }}</strong> akan dihapus.
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="deleting"
            @click="deleteDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="error"
            :loading="deleting"
            @click="doDelete"
          >
            Hapus
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="generatedPasswordDialog"
      max-width="480"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-2">
          Akun Login Dibuat
        </VCardTitle>
        <VCardText class="pa-6 pt-0">
          <VAlert
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            Catat password ini sekarang — tidak akan ditampilkan lagi setelah dialog ini ditutup.
          </VAlert>
          <p class="mb-1">
            <strong>Email:</strong> {{ generatedPasswordInfo.email }}
          </p>
          <p class="mb-0">
            <strong>Password:</strong> <code>{{ generatedPasswordInfo.password }}</code>
          </p>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            color="primary"
            @click="generatedPasswordDialog = false"
          >
            Tutup
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
